if (!customElements.get('product-size-guide')) {
  customElements.define('product-size-guide', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) { this.resizeObserver.observe(this.zoomScroll); return; }
      this.initialized = true;
      this.guide = this.querySelector('dialog');
      this.zoom = this.querySelector('.size-guide__zoom');
      this.opener = this.querySelector('.size-guide__trigger');
      this.content = this.querySelector('.size-guide__content');
      this.zoomScroll = this.querySelector('.size-guide__zoom-scroll');
      this.zoomCanvas = this.querySelector('.size-guide__zoom-canvas');
      this.zoomRange = this.querySelector('.size-guide__zoom-range');
      this.zoomReset = this.querySelector('.size-guide__zoom-reset');
      this.zoomRange.addEventListener('input', () => this.setZoom(Number(this.zoomRange.value)));
      this.zoomRange.addEventListener('change', event => event.stopPropagation());
      this.resizeObserver = new ResizeObserver(() => this.setZoom(Number(this.zoomRange.value)));
      this.resizeObserver.observe(this.zoomScroll);
      this.content.querySelectorAll('table').forEach(table => {
        const wrapper = document.createElement('div');
        wrapper.className = 'size-guide__table';
        table.before(wrapper);
        wrapper.append(table);
      });
      this.content.querySelectorAll('img').forEach(image => {
        if (image.closest('a')) return;
        const link = document.createElement('a');
        link.href = image.currentSrc || image.src;
        image.before(link);
        link.append(image);
      });
      this.content.querySelectorAll('a[href]').forEach(link => {
        if (!URL.canParse(link.href, location.href)) return;
        const url = new URL(link.href, location.href);
        if (!/^https?:$/.test(url.protocol) || !/\.(jpe?g|png|webp|gif|avif)$/i.test(url.pathname)) return;
        link.classList.add('size-guide__image-link');
        link.setAttribute('aria-haspopup', 'dialog');
        if (link.querySelector('img')) {
          link.setAttribute('aria-label', this.dataset.viewLabel + ' ' + link.querySelector('img').alt);
          const label = document.createElement('span');
          label.className = 'size-guide__view';
          label.textContent = this.dataset.viewLabel;
          link.append(label);
        }
      });
      this.addEventListener('click', event => {
        const target = event.target.closest('a, button');
        if (target === this.opener && typeof this.guide.showModal === 'function') {
          event.preventDefault();
          event.stopPropagation();
          this.previousOverflow = document.body.style.overflow;
          this.guide.showModal();
          document.body.style.overflow = 'hidden';
        } else if (target?.classList.contains('size-guide__close')) {
          event.stopPropagation();
          target.closest('dialog').close();
        } else if (target?.classList.contains('size-guide__image-link')) {
          event.preventDefault();
          event.stopPropagation();
          const image = new Image();
          image.alt = target.querySelector('img')?.alt || target.textContent.trim();
          image.src = target.href;
          image.addEventListener('load', () => this.setZoom(Number(this.zoomRange.value)), {once: true});
          this.zoomCanvas.replaceChildren(image);
          this.zoom.querySelector('.size-guide__original').href = target.href;
          this.zoom.showModal();
          this.setZoom(100);
          this.zoomScroll.scrollTo(0, 0);
        } else if (target?.hasAttribute('data-zoom-step')) {
          this.setZoom(Number(this.zoomRange.value) + Number(target.dataset.zoomStep), true);
        } else if (target === this.zoomReset) {
          this.setZoom(100, true);
        }
      });
      this.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        this.escapeFromDialog = this.guide.open || this.zoom.open;
        if (this.escapeFromDialog) event.stopPropagation();
      });
      this.addEventListener('keyup', event => {
        // 原生 dialog 关闭后焦点已转移，仍需隔离同一次 Escape 的松键事件。
        if (event.key !== 'Escape' || !this.escapeFromDialog) return;
        this.escapeFromDialog = false;
        event.stopPropagation();
      });
      [this.guide, this.zoom].forEach(dialog => dialog.addEventListener('click', event => {
        if (event.target !== dialog) return;
        const rect = dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) dialog.close();
      }));
      this.guide.addEventListener('close', () => {
        this.zoom.close();
        document.body.style.overflow = this.previousOverflow;
        if (this.isConnected) this.opener.focus({preventScroll: true});
      });
    }
    setZoom(percent, animate = false) {
      percent = Math.min(300, Math.max(100, percent));
      this.zoomRange.value = percent;
      this.zoomRange.setAttribute('aria-valuetext', percent + '%');
      this.zoomReset.textContent = percent + '%';
      this.querySelector('[data-zoom-step="-25"]').disabled = percent === 100;
      this.querySelector('[data-zoom-step="25"]').disabled = percent === 300;
      const image = this.zoomCanvas.querySelector('img');
      if (!this.zoom.open || !image?.naturalWidth) return;
      const viewport = this.zoomScroll;
      const oldWidth = image.offsetWidth;
      const oldHeight = image.offsetHeight;
      const x = (viewport.scrollLeft + viewport.clientWidth / 2 - (viewport.scrollWidth - oldWidth) / 2) / (oldWidth || 1);
      const y = (viewport.scrollTop + viewport.clientHeight / 2 - (viewport.scrollHeight - oldHeight) / 2) / (oldHeight || 1);
      const fit = Math.min(viewport.clientWidth / image.naturalWidth, viewport.clientHeight / image.naturalHeight);
      const width = image.naturalWidth * fit * percent / 100;
      const height = image.naturalHeight * fit * percent / 100;
      image.getAnimations().forEach(animation => animation.cancel());
      image.style.width = width + 'px';
      image.style.height = height + 'px';
      viewport.scrollLeft = x * width + (viewport.scrollWidth - width) / 2 - viewport.clientWidth / 2;
      viewport.scrollTop = y * height + (viewport.scrollHeight - height) / 2 - viewport.clientHeight / 2;
      if (animate && oldWidth && !matchMedia('(prefers-reduced-motion: reduce)').matches) {
        image.animate([{transform: `scale(${oldWidth / width})`}, {transform: 'scale(1)'}], {duration: 160, easing: 'ease-out'});
      }
    }
    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      if (this.guide?.open) this.guide.close();
      if (this.zoom?.open) this.zoom.close();
    }
  });
}
